/**
 * MeElderKids — Phase 5 (Iter 160)
 *   /me/health/elder  — Elder Care hub (vitals · pills · vaccine · SOS · wellness score)
 *   /me/health/kids   — Kids Care hub (age 5-18 · BMI · habits gamified · SOS ID)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Heart, Pill, Syringe, Users, ChartLineUp, Star,
  Thermometer, Drop, Pulse, Scales, FirstAid, Baby,
  Book, Trophy, Sparkle, Backpack, GraduationCap,
} from "@phosphor-icons/react";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function BackHeader({ to, title, subtitle, tint = "#DC2626" }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight" style={{ color: tint }}>
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Elder Care
// ══════════════════════════════════════════════════════════════════
export function ElderCare() {
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);

  const loadMembers = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = (r.data.loved_ones || []).filter(l => l.kind === "elder");
      setMembers(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) { setSummary(null); return; }
    try {
      const r = await custApi.get(`/me/elder/${selectedId}/summary`);
      setSummary(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadMembers(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [selectedId]);

  if (members.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4">
        <BackHeader to="/me/health" title="Elder Care" tint="#DC2626"/>
        <EmptyState kind="elder" tint="#DC2626" bg="#FEE2E2"
                    label="Add an elder loved one"
                    hint="On Profile → Health Profile, add someone tagged as Elder (parents/grandparents)."/>
      </div>
    );
  }

  const lo = summary?.loved_one;
  const v = summary?.vitals_latest || {};
  const score = summary?.wellness_score ?? 0;
  const scoreColor = score >= 80 ? "#15803D" : score >= 60 ? "#EA580C" : "#DC2626";

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-elder">
      <BackHeader to="/me/health" title="Elder Care" subtitle="Vitals · meds · appointments"/>

      {/* Picker */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {members.map(m => {
          const on = m.id === selectedId;
          return (
            <button key={m.id} onClick={() => setSelectedId(m.id)}
                    data-testid={`elder-picker-${m.id}`}
                    className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 ${
                      on ? "bg-[#DC2626] text-white border-[#DC2626]"
                         : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                on ? "bg-white/25" : "bg-[#FEE2E2] text-[#DC2626]"
              }`}>{m.name?.[0]?.toUpperCase() || "?"}</span>
              {m.name}
            </button>
          );
        })}
      </div>

      {lo && (
        <div className="rounded-3xl p-5 bg-gradient-to-br from-[#FEE2E2] via-[#FCA5A5] to-[#EF4444] text-white shadow-[0_20px_50px_rgba(220,38,38,0.20)]"
             data-testid="elder-hero">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur inline-flex items-center justify-center text-2xl font-black">
              {lo.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-2xl leading-tight">{lo.name}</div>
              <div className="text-[11px] opacity-95 mt-0.5 font-medium">
                {lo.age ? `${lo.age}y` : ""} {lo.relation} {lo.blood_group ? `· ${lo.blood_group}` : ""}
              </div>
              {(lo.diseases || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {lo.diseases.map(d => (
                    <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-white/25 font-bold">{d}</span>
                  ))}
                </div>
              )}
            </div>
            {/* Wellness score */}
            <div className="text-center shrink-0 bg-white rounded-2xl p-2 min-w-[64px]">
              <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Health</div>
              <div className="font-display font-black text-2xl leading-none mt-0.5" style={{ color: scoreColor }}>
                {score}
              </div>
              <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: scoreColor }}>
                score
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vital cards */}
      <div className="grid grid-cols-2 gap-2">
        <VitalCard Icon={Heart} label="Blood Pressure"
                    val={v.bp ? `${v.bp.value_systolic}/${v.bp.value_diastolic}` : "—"}
                    unit="mmHg" color="#DC2626" bg="#FEE2E2"/>
        <VitalCard Icon={Drop} label="Blood Sugar"
                    val={v.sugar ? String(v.sugar.value) : "—"}
                    unit="mg/dL" color="#B45309" bg="#FEF3C7"/>
        <VitalCard Icon={Pulse} label="Oxygen"
                    val={v.oxygen ? String(v.oxygen.value) : "—"}
                    unit="%" color="#0F766E" bg="#CCFBF1"/>
        <VitalCard Icon={Scales} label="Weight"
                    val={v.weight ? String(v.weight.value) : "—"}
                    unit="kg" color="#7C3AED" bg="#EDE9FE"/>
      </div>

      {/* Action grid */}
      <div className="grid grid-cols-4 gap-2">
        <ActionTile to="/me/health/vitals" Icon={Heart} label="Log vital" color="#DC2626" bg="#FEE2E2"/>
        <ActionTile to="/me/health/pills" Icon={Pill} label="Medicines"
                    hint={summary?.active_pill_count > 0 ? `${summary.active_pill_count} active` : ""}
                    color="#EA580C" bg="#FFEDD5"/>
        <ActionTile to="/me/health/vaccines" Icon={Syringe} label="Vaccines"
                    hint={summary?.next_vaccine ? "1 due" : ""}
                    color="#2563EB" bg="#DBEAFE"/>
        <ActionTile to="/me/health/emergency" Icon={FirstAid} label="SOS 108" color="#7F1D1D" bg="#FEE2E2"/>
        <ActionTile to="/me/health/bookings" Icon={Users} label="Doctor" color="#0F766E" bg="#DCFCE7"/>
        <ActionTile to="/me/health/circle" Icon={Book} label="Tips" color="#7C3AED" bg="#EDE9FE"/>
      </div>

      {/* Active pills preview */}
      {summary?.active_pills?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
            Today&apos;s medicines
          </div>
          <div className="space-y-1.5">
            {summary.active_pills.slice(0, 5).map(p => (
              <div key={p.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-md p-2.5">
                <div className="w-9 h-9 rounded-full bg-[#FEE2E2] text-[#DC2626] inline-flex items-center justify-center">
                  <Pill size={14} weight="fill"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">
                    {p.medicine_name}
                    {p.dosage && <span className="ml-1 text-[10px] text-gray-500">{p.dosage}</span>}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {(p.times_of_day || []).join(" · ") || (p.interval_hours ? `every ${p.interval_hours}h` : "")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VitalCard({ Icon, label, val, unit, color, bg }) {
  return (
    <div className="rounded-2xl p-3 border" style={{ backgroundColor: bg, borderColor: color + "22" }}>
      <Icon size={14} weight="fill" color={color}/>
      <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5" style={{ color }}>{label}</div>
      <div className="font-display font-black text-lg mt-1" style={{ color }}>{val}</div>
      <div className="text-[9px] font-semibold" style={{ color }}>{unit}</div>
    </div>
  );
}

function ActionTile({ to, Icon, label, hint, color, bg }) {
  return (
    <Link to={to} data-testid={`et-${label.toLowerCase().replace(/ /g,"-")}`}
          className="bg-white rounded-2xl p-3 border border-gray-100 shadow-sm hover:shadow-md active:scale-[0.97] transition-all text-center">
      <div className="w-10 h-10 rounded-2xl inline-flex items-center justify-center mx-auto"
           style={{ backgroundColor: bg, color }}>
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="text-[11px] font-semibold text-[#1A1A1A] mt-1.5 leading-tight">{label}</div>
      {hint && <div className="text-[9px] font-bold uppercase tracking-widest mt-0.5" style={{ color }}>{hint}</div>}
    </Link>
  );
}

function EmptyState({ tint, bg, label, hint }) {
  return (
    <div className="border border-dashed rounded-md p-4 text-center"
         style={{ borderColor: tint, backgroundColor: bg }}>
      <div className="text-sm font-semibold text-[#1A1A1A]">{label}</div>
      <div className="text-[11px] text-[#5C5A55] mt-1">{hint}</div>
      <Link to="/me/profile#health-profile"
            className="mt-3 inline-block h-9 px-4 leading-9 text-white rounded-md text-xs font-medium"
            style={{ backgroundColor: tint }}>
        Go to Profile
      </Link>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Kids Care (age 5-18)
// ══════════════════════════════════════════════════════════════════
export function KidsCare() {
  const [kids, setKids] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);

  const loadKids = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = (r.data.loved_ones || []).filter(l => l.kind === "kid");
      setKids(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) { setSummary(null); return; }
    try {
      const r = await custApi.get(`/me/kid/${selectedId}/summary`);
      setSummary(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadKids(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [selectedId]);

  const toggle = async (habit_key) => {
    try {
      await custApi.post("/me/kid-habits/toggle", {
        loved_one_id: selectedId, habit_key, date: todayISO(),
      });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (kids.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4">
        <BackHeader to="/me/health" title="Kids Care" tint="#7C3AED"/>
        <EmptyState tint="#7C3AED" bg="#EDE9FE"
                    label="Add your child (age 5–18)"
                    hint="On Profile → Health Profile, add a loved one with Care Kind = Kid."/>
      </div>
    );
  }

  const lo = summary?.loved_one;
  const bmi = summary?.bmi;
  const bmiBand = summary?.bmi_band;
  const bmiColors = {
    underweight: { c: "#EA580C", bg: "#FFEDD5" },
    healthy:     { c: "#15803D", bg: "#DCFCE7" },
    overweight:  { c: "#B45309", bg: "#FEF3C7" },
    obese:       { c: "#DC2626", bg: "#FEE2E2" },
  };
  const bmiColor = bmiBand ? bmiColors[bmiBand] : { c: "#8A8780", bg: "#F3F4F6" };

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-kids">
      <BackHeader to="/me/health" title="Kids Care" subtitle="Age 5–18 · daily habits · growth" tint="#7C3AED"/>

      {/* Picker */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {kids.map(k => {
          const on = k.id === selectedId;
          return (
            <button key={k.id} onClick={() => setSelectedId(k.id)}
                    data-testid={`kid-picker-${k.id}`}
                    className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 ${
                      on ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                         : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                on ? "bg-white/25" : "bg-[#EDE9FE] text-[#7C3AED]"
              }`}>{k.name?.[0]?.toUpperCase() || "?"}</span>
              {k.name}
            </button>
          );
        })}
      </div>

      {lo && summary && (
        <div className="rounded-3xl p-5 bg-gradient-to-br from-[#EDE9FE] via-[#DDD6FE] to-[#A78BFA] shadow-[0_20px_50px_rgba(124,58,237,0.20)]"
             data-testid="kid-hero">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white text-[#7C3AED] font-black text-2xl inline-flex items-center justify-center">
              {lo.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl text-[#4C1D95]">{lo.name}</div>
              <div className="text-[11px] text-[#5B21B6] font-medium">
                {summary.age_years != null ? `${summary.age_years} years` : "—"}
                {lo.gender ? ` · ${lo.gender}` : ""}
                {lo.blood_group ? ` · ${lo.blood_group}` : ""}
              </div>
            </div>
            <div className="text-center bg-white rounded-2xl p-2 min-w-[64px]">
              <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Dev</div>
              <div className="font-display font-black text-2xl leading-none text-[#7C3AED]">{summary.development_score}</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#7C3AED]">score</div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="bg-white/60 backdrop-blur rounded-xl p-2 text-center">
              <div className="text-[9px] uppercase tracking-widest font-bold text-[#5B21B6]">BMI</div>
              <div className="font-display font-black text-lg text-[#4C1D95]">{bmi ?? "—"}</div>
              {bmiBand && (
                <div className="text-[9px] font-bold uppercase tracking-widest mt-0.5 inline-block px-1.5 py-0.5 rounded"
                     style={{ color: bmiColor.c, backgroundColor: bmiColor.bg }}>
                  {bmiBand}
                </div>
              )}
            </div>
            <div className="bg-white/60 backdrop-blur rounded-xl p-2 text-center">
              <div className="text-[9px] uppercase tracking-widest font-bold text-[#5B21B6]">Stars today</div>
              <div className="font-display font-black text-lg text-[#4C1D95]">{summary.stars_today}/{summary.total_habits}</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#7C3AED] mt-0.5">habits</div>
            </div>
            <div className="bg-white/60 backdrop-blur rounded-xl p-2 text-center">
              <div className="text-[9px] uppercase tracking-widest font-bold text-[#5B21B6]">Streak</div>
              <div className="font-display font-black text-lg text-[#4C1D95]">{summary.streak_days}d</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#7C3AED] mt-0.5"><Trophy size={9} className="inline"/> hot</div>
            </div>
          </div>
        </div>
      )}

      {/* Today's habits — gamified */}
      {summary?.habits_catalog && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
            Today&apos;s habits · tap to tick
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="kid-habits">
            {summary.habits_catalog.map(h => (
              <button key={h.key}
                      data-testid={`kid-habit-${h.key}`}
                      onClick={() => toggle(h.key)}
                      className={`rounded-2xl p-3 text-center transition-all active:scale-[0.95] ${
                        h.done_today
                          ? "bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white shadow-md"
                          : "bg-white border border-gray-100 text-[#1A1A1A]"
                      }`}>
                <div className="text-2xl leading-none">{h.emoji}</div>
                <div className="text-[10px] font-semibold mt-1 leading-tight">{h.label}</div>
                {h.done_today && <Star size={10} weight="fill" className="mx-auto mt-1 text-yellow-300"/>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feature grid */}
      <div className="grid grid-cols-4 gap-2">
        <ActionTile to="/me/health/growth" Icon={ChartLineUp} label="Growth" color="#2563EB" bg="#DBEAFE"/>
        <ActionTile to="/me/health/vaccines" Icon={Syringe} label="Vaccines"
                    hint={summary?.next_vaccine ? "1 due" : ""} color="#15803D" bg="#DCFCE7"/>
        <ActionTile to="/me/health/vitals" Icon={Heart} label="Vitals" color="#DC2626" bg="#FEE2E2"/>
        <ActionTile to="/me/health/pills" Icon={Pill} label="Meds"
                    hint={summary?.active_pill_count > 0 ? `${summary.active_pill_count} on` : ""}
                    color="#EA580C" bg="#FFEDD5"/>
        <ActionTile to="/me/health/bookings" Icon={GraduationCap} label="Doctor" color="#0891B2" bg="#CFFAFE"/>
        <ActionTile to="/me/health/circle" Icon={Book} label="Parenting" color="#B45309" bg="#FEF3C7"/>
        <ActionTile to="/me/health/emergency" Icon={FirstAid} label="SOS" color="#7F1D1D" bg="#FEE2E2"/>
        <ActionTile to="#" Icon={Backpack} label="School" color="#7C3AED" bg="#EDE9FE"/>
      </div>

      {/* Emergency Child ID card */}
      {lo && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] text-white shadow-[0_10px_25px_rgba(220,38,38,0.25)]"
             data-testid="kid-id-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 inline-flex items-center justify-center">
              <FirstAid size={22} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Emergency ID</div>
              <div className="font-display font-black text-lg leading-tight">{lo.name}</div>
              <div className="text-[11px] opacity-95">
                {summary?.age_years ?? "—"}y {lo.gender ? `· ${lo.gender}` : ""}
                {lo.blood_group ? ` · Blood ${lo.blood_group}` : ""}
              </div>
              {(lo.diseases || []).length > 0 && (
                <div className="text-[10px] mt-1 opacity-90">
                  Conditions: {lo.diseases.join(", ")}
                </div>
              )}
            </div>
            <Link to="/me/health/emergency" className="h-9 px-3 bg-white text-[#DC2626] rounded-full text-xs font-bold inline-flex items-center">
              108
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
